import { T_PANEL, CELL_FILLS, cellKey, makeSourceV2 } from './schema';
import { computeColRects, computeRowRects, innerRect } from './grid';
import { fillingToModules } from './cells';
import { COLORS } from '@/lib/furnitureData';
import { getCategoryCatalog } from '@/lib/catalog/categories';

// Компилирует параметрический source → плоский modules[] для рендера и экспорта.
// Совместимо с текущим рендерингом и PDF — шаг ничего не знает про новую схему.
function compileSourceV1(source) {
  if (!source || !source.body || !source.grid) return [];
  const body = source.body;
  const grid = source.grid;
  const cells = source.cells || {};
  const col = COLORS.find(c => c.id === body.color) || COLORS[0];
  const base = {
    material: body.material || 'mdf',
    color_id: body.color || 'light_oak',
    color_hex: col.hex || '#C4A76C',
  };

  const modules = [];
  const id = () => Math.random().toString(36).slice(2, 11);
  const push = (m) => modules.push({ id: id(), rotation: 0, base_price: 0, hardware: [], ...m });

  // 1. Корпус — цельный габарит, как раньше (рендер рисует его 4 стенками)
  push({
    type: 'body', name_ru: 'Корпус',
    width: body.width, height: body.height, depth: body.depth,
    x: 0, y: 0,
    ...base,
    base_price: 5000,
  });

  // 2. Задняя стенка (внутри фальца)
  if (body.hasBack !== false) {
    push({
      type: 'back_panel', name_ru: 'Задняя стенка',
      width: body.width - 2, height: body.height - 2, depth: 0.3,
      x: 1, y: 1, ...base, base_price: 800,
    });
  }

  // 3. Перегородки (между колонками) — вертикальные панели на всю внутреннюю высоту
  const colRects = computeColRects(body, grid);
  const rowRects = computeRowRects(body, grid);
  const inner = innerRect(body);

  for (let i = 0; i < colRects.length - 1; i++) {
    const r = colRects[i];
    const divX = r.x + r.w;   // ровно за колонкой i
    push({
      type: 'shelf', // «shelf» в терминах существующего рендера = вертикальная/горизонтальная панель
      name_ru: `Перегородка ${i + 1}`,
      width: T_PANEL, height: inner.h, depth: body.depth - 2,
      x: divX, y: inner.y,
      ...base, base_price: 450,
    });
  }

  // 4. Горизонты (между рядами) — идут сквозь все колонки (от левой стенки до правой),
  //    но разрываются перегородками. Мы упрощаем: рисуем горизонт в каждой колонке отдельно.
  for (let r = 0; r < rowRects.length - 1; r++) {
    const divY = rowRects[r].y + rowRects[r].h;
    for (let c = 0; c < colRects.length; c++) {
      push({
        type: 'shelf',
        name_ru: `Горизонт ${r + 1} (кол. ${c + 1})`,
        width: colRects[c].w, height: T_PANEL, depth: body.depth - 2,
        x: colRects[c].x, y: divY,
        ...base, base_price: 400,
      });
    }
  }

  // 5. Наполнение ячеек
  for (let c = 0; c < colRects.length; c++) {
    for (let r = 0; r < rowRects.length; r++) {
      const cell = cells[cellKey(c, r)] || { fill: CELL_FILLS.empty };
      const rect = { x: colRects[c].x, y: rowRects[r].y, w: colRects[c].w, h: rowRects[r].h };
      const subs = fillingToModules(rect, cell, body, c * rowRects.length + r);
      subs.forEach(push);
    }
  }

  return modules;
}

function resolveAbsPosition(partsById, part) {
  let x = Number(part.localBox?.x || 0);
  let y = Number(part.localBox?.y || 0);
  let z = Number(part.localBox?.z || 0);
  let cursor = part;
  while (cursor?.parentId) {
    const parent = partsById.get(cursor.parentId);
    if (!parent) break;
    x += Number(parent.localBox?.x || 0);
    y += Number(parent.localBox?.y || 0);
    z += Number(parent.localBox?.z || 0);
    cursor = parent;
  }
  return { x, y, z };
}

function partTypeToModuleType(partType) {
  const map = {
    facades: 'door',
    shelf_board: 'board',
    supports: 'leg',
    top: 'top_panel',
    vertical_stands: 'shelf',
    horizontal_shelves: 'shelf',
    drawer_stack: 'drawer',
    holders: 'bracket',
  };
  return map[partType] || partType;
}

export function compileSourceV2(source) {
  if (!source || source.kind !== 'parametric-v2') return [];
  const colorId = source.attributes?.body_color || 'light_oak';
  const color = COLORS.find((c) => c.id === colorId) || COLORS[0];
  const material = source.attributes?.body_material || 'mdf';
  const parts = source.parts || [];
  const partsById = new Map(parts.map((p) => [p.id, p]));

  return parts.map((part) => {
    const abs = resolveAbsPosition(partsById, part);
    const box = part.localBox || {};
    const effective = part.effectiveAttributes || part.attributes || {};
    return {
      id: part.id,
      type: partTypeToModuleType(part.partType),
      name_ru: part.name_ru || part.partType,
      width: Number(box.w || 0),
      height: Number(box.h || 0),
      depth: Number(box.d || 0),
      x: abs.x,
      y: abs.y,
      z_pos: abs.z,
      rotation: 0,
      material: effective.body_material || material,
      color_id: effective.body_color || color.id,
      color_hex: color.hex,
      hardware: [],
      base_price: Number(part.base_price || 500),
    };
  });
}

export function compileSource(source) {
  if (!source) return [];
  if (source.kind === 'parametric-v2') return compileSourceV2(source);
  return compileSourceV1(source);
}

export function modulesToSourceV2(modules = [], category = 'wardrobe') {
  if (!Array.isArray(modules) || modules.length === 0) return makeSourceV2({ category });
  const body = modules.find((m) => ['body', 'frame', 'tabletop', 'countertop', 'headboard'].includes(m.type)) || modules[0];
  const width = Number(body.width || 120);
  const height = Number(body.height || 100);
  const depth = Number(body.depth || 60);
  const source = makeSourceV2({
    category,
    width,
    height,
    depth,
    attributes: {
      body_color: body.color_id || 'light_oak',
      body_material: body.material || 'mdf',
    },
  });
  const rootBody = source.parts[0];
  const bodyPartId = body.id || rootBody.id;
  const extras = modules
    .filter((m) => m.id !== body.id)
    .map((m) => ({
      id: m.id || `${m.type}_${Math.random().toString(36).slice(2, 8)}`,
      partType: m.type,
      parentId: bodyPartId,
      localBox: {
        x: Number(m.x || 0),
        y: Number(m.y || 0),
        z: Number(m.z_pos || 0),
        w: Number(m.width || 0),
        h: Number(m.height || 0),
        d: Number(m.depth || 0),
      },
      socketRef: m.type === 'door' ? 'frontPlane' : 'innerContainer',
      attributes: {
        body_color: m.color_id,
        body_material: m.material,
      },
      constraints: {
        requiresSupport: m.type === 'shelf' || m.type === 'board',
      },
    }));

  const catalog = getCategoryCatalog(category);
  return {
    ...source,
    required_parts: catalog?.required_parts || [],
    optional_parts: catalog?.optional_parts || [],
    parts: [
      {
        ...rootBody,
        id: bodyPartId,
        partType: body.type || 'body',
        localBox: { x: Number(body.x || 0), y: Number(body.y || 0), z: Number(body.z_pos || 0), w: width, h: height, d: depth },
      },
      ...extras,
    ],
  };
}
